import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import EmptyList from 'components/common/EmptyList';
import Flex from 'components/common/Flex';
import IconButton from 'components/common/IconButton';
import CustomerDomainEditModal from 'components/modals/CustomerDomainEditModal';
import CustomerDomainRegistrationModal from 'components/modals/CustomerDomainRegistrationModal';
import DialogModal from 'components/modals/DialogModal';
import Pagination from 'components/pagination';
import useApi from 'helpers/api';
import useAdvanceTable from 'hooks/useAdvanceTable';
import useNotification from 'hooks/useNotification';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { useCallback, useEffect, useState } from 'react';
import { Col } from 'react-bootstrap';

const CustomerDomains = ({ data }) => {
  const api = useApi();
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const [customer, setCustomer] = useState({});
  const [domains, setDomains] = useState({ content: [] });
  const [selectedDomain, setSelectedDomain] = useState({});
  const [isEmpty, setIsEmpty] = useState(false);
  const [showModalDomainRegistration, setShowModalDomainRegistration] =
    useState(false);
  const [showModalEditDomain, setShowModalEditDomain] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [filterDomains, setFilterDomains] = useState({ page: 0, size: 10 });

  // Initialize customer data
  useEffect(() => {
    setCustomer(data);
  }, [data]);

  // Fetch domains when customer or filters change
  useEffect(() => {
    if (Object.keys(customer).length !== 0) {
      fetchDomains();
    }
  }, [customer, filterDomains]);

  // CRUD operations for domains
  const fetchDomains = useCallback(async () => {
    const queryParams = new URLSearchParams(filterDomains).toString();
    const response = await api.allowedDomains.list(customer.id, queryParams);

    if (response.userMessage) {
      showErrorNotification(response.userMessage || 'Failed to fetch domains');
    } else {
      setDomains(response);
      setIsEmpty(response.content.length === 0);
    }
  }, [api.allowedDomains, customer.id, filterDomains, showErrorNotification]);

  const addDomain = async body => {
    const response = await api.allowedDomains.add(customer.id, body);

    if (response.userMessage) {
      console.log(response);
      showErrorNotification(response.userMessage || 'Failed to add domain');
    } else {
      showSuccessNotification('Domínio liberado com sucesso');
      fetchDomains();
      handleCloseModalDomainRegistration();
    }
  };

  const editDomain = async body => {
    const response = await api.allowedDomains.edit(
      customer.id,
      selectedDomain.id,
      body
    );

    if (response.userMessage) {
      showErrorNotification(response.userMessage || 'Failed to edit domain');
    } else {
      showSuccessNotification('Domínio editado com sucesso');
      fetchDomains();
      handleCloseModalEditDomain();
    }
  };

  const deleteDomain = async confirm => {
    if (!confirm) {
      handleCloseDialog();
      return;
    }

    const response = await api.allowedDomains.delete(
      customer.id,
      selectedDomain.id
    );

    if (response.userMessage) {
      showErrorNotification(response.userMessage || 'Failed to delete domain');
    } else {
      showSuccessNotification('Domínio excluído com sucesso');
      fetchDomains();
      handleCloseDialog();
    }
  };

  const handleEditDomain = item => {
    setSelectedDomain(item);
    setShowModalEditDomain(true);
  };

  const handleDeleteDomain = item => {
    setSelectedDomain(item);
    setShowDialog(true);
  };

  const handleCloseModalDomainRegistration = () =>
    setShowModalDomainRegistration(false);
  const handleCloseModalEdit = () => setShowModalEdit(false);
  const handleCloseModalEditDomain = () => setShowModalEditDomain(false);
  const handleCloseDialog = () => setShowDialog(false);

  // Table configurations
  const domainColumns = [
    {
      accessorKey: 'domain',
      header: 'Domínio',
      meta: { headerProps: { className: 'text-900' } }
    },
    {
      accessorKey: 'actions',
      header: 'Ações',
      meta: { headerProps: { className: 'text-900' } },
      cell: ({ row }) => (
        <>
          <IconButton
            className="ms-2"
            iconClassName="fs--2"
            variant="link"
            size="sm"
            icon="pencil-alt"
            onClick={() => handleEditDomain(row.original)}
          />
          <IconButton
            className="ms-2"
            iconClassName="fs--2"
            variant="link"
            size="sm"
            icon="trash"
            onClick={() => handleDeleteDomain(row.original)}
          />
        </>
      )
    }
  ];
  const table = useAdvanceTable({
    data: domains.content,
    columns: domainColumns
  });
  return (
    <>
      <CustomerDomainRegistrationModal
        showModal={showModalDomainRegistration}
        handleClose={handleCloseModalDomainRegistration}
        handleSubmit={addDomain}
      />
      <CustomerDomainEditModal
        data={selectedDomain}
        showModal={showModalEditDomain}
        handleClose={handleCloseModalEditDomain}
        handleSubmit={editDomain}
      />
      <DialogModal
        message="Tem certeza que deseja excluir este domínio?"
        showModal={showDialog}
        handleClose={handleCloseDialog}
        response={deleteDomain}
      />
      <Flex
        justifyContent={'between'}
        alignItems={'center'}
        className="w-100 pb-2"
      >
        <Col className="col-auto">
          <h6 className="fw-semi-bold ls ps-3 mb-3 text-uppercase">
            Domínios liberados
          </h6>
        </Col>
        <Col className="col-auto">
          <IconButton
            iconClassName="fs--2 me-1"
            variant="primary"
            size="sm"
            icon="plus"
            onClick={() => setShowModalDomainRegistration(true)}
          >
            Novo
          </IconButton>
        </Col>
      </Flex>

      {isEmpty ? (
        <EmptyList imgSize={350} vhSize={60} />
      ) : (
        <>
          <AdvanceTableProvider {...table}>
            <AdvanceTable
              table
              headerClassName="text-nowrap align-middle"
              rowClassName="align-middle white-space-nowrap"
              tableProps={{
                striped: true,
                className: 'fs-10 mb-0 overflow-hidden'
              }}
            />
          </AdvanceTableProvider>

          <Pagination
            data={domains}
            filters={filterDomains}
            setFilters={setFilterDomains}
          />
        </>
      )}
    </>
  );
};

export default CustomerDomains;
